import { inject, Service } from '@angular/core';
import { EventBusService } from '@shared';
import { OmsOrderState, PrivateMessage, PrivateMessageAction } from '../../core/private-messages/private-message.model';
import {
	PRIVATE_MESSAGE_EVENTS,
	PrivateMessageReloadEvent,
	PrivateMessageTextEvent
} from './private-message-events.model';

/**
 * Publishes private-message events onto the shared event bus.
 *
 * Every dispatch is fire-and-forget: the bus is `BehaviorSubject`-backed, and
 * these are all *notifications* — "this just happened", "refetch now". Leaving a
 * snapshot behind would replay a stale one into any consumer that subscribed
 * later, so a page opened an hour after the fact would reload for a message it
 * never received.
 *
 * Written to by {@link PrivateMessageService} only; read via
 * {@link PrivateMessageEventConsumerService}.
 */
@Service()
export class PrivateMessageEventProducerService {
	private readonly eventBus = inject(EventBusService);

	received(message: PrivateMessage): void {
		this.eventBus.dispatch(PRIVATE_MESSAGE_EVENTS.RECEIVED, message, true);
	}

	textReceived(text: PrivateMessageTextEvent): void {
		this.eventBus.dispatch(PRIVATE_MESSAGE_EVENTS.TEXT_RECEIVED, text, true);
	}

	orderStateChanged(orderState: OmsOrderState): void {
		this.eventBus.dispatch(PRIVATE_MESSAGE_EVENTS.ORDER_STATE_CHANGED, orderState, true);
	}

	ordersReloadRequested(reason: PrivateMessageAction | 'reconnect'): void {
		this.eventBus.dispatch<PrivateMessageReloadEvent>(
			PRIVATE_MESSAGE_EVENTS.ORDERS_RELOAD_REQUESTED,
			{ reason },
			true
		);
	}

	remainReloadRequested(reason: PrivateMessageAction | 'reconnect'): void {
		this.eventBus.dispatch<PrivateMessageReloadEvent>(
			PRIVATE_MESSAGE_EVENTS.REMAIN_RELOAD_REQUESTED,
			{ reason },
			true
		);
	}

	depositPortfolioReloadRequested(reason: PrivateMessageAction): void {
		this.eventBus.dispatch<PrivateMessageReloadEvent>(
			PRIVATE_MESSAGE_EVENTS.DEPOSIT_PORTFOLIO_RELOAD_REQUESTED,
			{ reason },
			true
		);
	}
}
